import { useCallback, useEffect, useMemo, useState } from 'react';

import isNil from 'lodash-es/isNil';
import { CircleHelpIcon } from 'lucide-react';
import { useForm, useController, Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import CodeBlock from '@/components/atoms/CodeBlock/CodeBlock';
import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import OptionRadioButton from '@/components/atoms/OptionRadioButton/OptionRadioButton';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { cn } from '@/lib/utils';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { languageAndFlagByCountryCode } from '@/shared/languageCodes';
import { OptionBase } from '@/shared/types/common';
import {
  AccordionFieldsetProps,
  Integration,
  IntegrationType,
  WebWidgetConfig,
  WebWidgetIntegration,
} from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getDocsUrl } from '@/util/constants';
import { formatLanguageLabel, prioritizeLanguageOptions } from '@/util/languageOptions';
import { decodeBase64, encodeBase64 } from '@/util/util';
import { isRequired } from '@/util/validation/constants';
import { errorMessage, trimOnBlur } from '@/util/validation/helpers';
import { customerRsaKey } from '@/util/validation/rules/integration-rules';

import styles from './AdvancedSettings.module.scss';

type Form = {
  customer_rsa_key: string;
  encryption_activation: boolean;
  sticky_session?: boolean;
  resume_timeout?: number | null;
  show_survey?: boolean;
  rate_responses?: boolean;
  close_minimizes_window?: boolean;
  inactivity_enabled?: boolean;
  inactivity_timeout?: number | null;
  language?: string;
  external_urls_limit?: number;
  file_upload?: boolean;
  floating_window?: boolean;
};

const SOURCES_MAX_LIMIT = 8;
const RESUME_TIMEOUT_MAX_MINUTES = 1440;
const INACTIVITY_TIMEOUT_MIN_MINUTES = 3;
const INACTIVITY_TIMEOUT_MAX_MINUTES = 30;
const INACTIVITY_TIMEOUT_DEFAULT_MINUTES = 10;

const sourcesLimitOptions = Array.from({ length: SOURCES_MAX_LIMIT }, (_, i) => ({
  label: `${i + 1}`,
  value: i + 1,
}));

const AdvancedSettings = ({
  type,
  integration,
  toggleAccordion,
  registerAccordion,
}: AccordionFieldsetProps<Form, Integration>) => {
  const integrationConfig = integration?.config as WebWidgetConfig;
  const { t } = useTranslation();
  const { expanded } = useSelector(selectAccordion);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const integration_id = integration?.integration_id;
  const desk_id = integration?.desk_id;
  const integrationType = integration?.type;
  const { updateIntegration, updateStatus } = useIntegrations<WebWidgetIntegration>(
    desk_id,
    integration_id
  );
  const dispatch = useDispatch();

  const [generatedPublicKey, setGeneratedPublicKey] = useState('');
  const [showSourceLimit, setShowSourceLimit] = useState(
    !isNil(integrationConfig?.external_urls_limit)
  );
  const [languageOptions, setLanguageOptions] = useState<OptionBase[]>();

  const formMethods = useForm<Form>({ mode: 'onSubmit' });
  const {
    control,
    reset,
    formState: { errors, isDirty },
    handleSubmit,
    trigger,
    setValue,
    getValues,
    clearErrors,
  } = formMethods;

  const { field } = useController({ name: 'sticky_session', control });

  const activation = useWatch({ name: 'encryption_activation', control });
  const inactivityEnabled = useWatch({ name: 'inactivity_enabled', control });

  useEffect(() => {
    reset({
      customer_rsa_key: decodeBase64(integrationConfig?.public_customer_rsa_key) || '',
      sticky_session: integrationConfig?.sticky_session ?? false,
      resume_timeout: integrationConfig?.resume_timeout ?? 0,
      encryption_activation: !!integrationConfig?.public_customer_rsa_key,
      show_survey: integrationConfig?.show_survey ?? true,
      language: integrationConfig?.language || 'default',
      external_urls_limit: integrationConfig?.external_urls_limit ?? undefined,
      file_upload: integrationConfig?.file_upload,
      floating_window: integrationConfig?.floating_window ?? false,
      rate_responses: integrationConfig?.rate_responses ?? true,
      close_minimizes_window: integrationConfig?.close_minimizes_window ?? false,
      inactivity_enabled: integrationConfig?.inactivity_timeout !== 0,
      inactivity_timeout:
        integrationConfig?.inactivity_timeout || INACTIVITY_TIMEOUT_DEFAULT_MINUTES,
    });
  }, [
    integrationConfig?.file_upload,
    integrationConfig?.floating_window,
    integrationConfig?.show_survey,
    integrationConfig?.language,
    integrationConfig?.public_customer_rsa_key,
    integrationConfig?.sticky_session,
    integrationConfig?.resume_timeout,
    integrationConfig?.external_urls_limit,
    reset,
    integrationConfig?.rate_responses,
    integrationConfig?.close_minimizes_window,
    integrationConfig?.inactivity_timeout,
  ]);

  useEffect(() => {
    if (integrationConfig?.generated_public_key) {
      setGeneratedPublicKey(decodeBase64(integrationConfig?.generated_public_key));
    }
  }, [integrationConfig?.generated_public_key]);

  const handleUpdateIntegration = useCallback(() => {
    const {
      customer_rsa_key,
      sticky_session,
      resume_timeout,
      encryption_activation,
      show_survey,
      language,
      external_urls_limit,
      file_upload,
      floating_window,
      rate_responses,
      close_minimizes_window,
      inactivity_enabled,
      inactivity_timeout,
    } = getValues();

    const webConfig =
      integrationType === IntegrationType.WEB
        ? {
            file_upload,
            // Off removes the key, which the widget reads as off.
            floating_window: floating_window || undefined,
            sticky_session,
            resume_timeout: resume_timeout || undefined,
            external_urls_limit,
            show_survey,
            language: language !== 'default' ? language : undefined,
            rate_responses,
            close_minimizes_window,
            inactivity_timeout: inactivity_enabled
              ? (inactivity_timeout ?? INACTIVITY_TIMEOUT_DEFAULT_MINUTES)
              : 0,
          }
        : {};
    updateIntegration(
      {
        integration_id,
        desk_id,
        type: IntegrationType.WEB,
        config: {
          ...integration?.config,
          ...webConfig,
          public_customer_rsa_key:
            customer_rsa_key && encryption_activation ? encodeBase64(customer_rsa_key) : undefined,
        },
      },
      {
        onSuccess: () => {
          if (expanded === type) {
            dispatch(setExpanded(false));
          }
        },
      }
    );
  }, [
    getValues,
    updateIntegration,
    integration_id,
    desk_id,
    integrationType,
    integration?.config,
    expanded,
    type,
    dispatch,
  ]);

  usePrompt(isDirty && canWrite, undefined, undefined, handleSubmit(handleUpdateIntegration));

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [expanded, isDirty, reset, type]);

  const stickyOptions = useMemo(
    () => [
      {
        id: 'sticky-enable',
        value: 'true',
        label: (
          <TitleSubtitle
            title={t('integrations.sticky_session.conv_hist.r_enable.title')}
            subtitle={t('integrations.sticky_session.conv_hist.r_enable.subtitle')}
            noGutters
          />
        ),
      },
      {
        id: 'sticky-disable',
        value: 'false',
        label: (
          <TitleSubtitle
            title={t('integrations.sticky_session.conv_hist.r_disable.title')}
            subtitle={t('integrations.sticky_session.conv_hist.r_disable.subtitle')}
            noGutters
          />
        ),
      },
    ],
    [t]
  );

  const resumeTimeoutRules = {
    min: { value: 0, message: t('validation.minValue', { 0: 0 }) },
    max: {
      value: RESUME_TIMEOUT_MAX_MINUTES,
      message: t('validation.maxValue', { 0: RESUME_TIMEOUT_MAX_MINUTES }),
    },
    validate: (value?: number | null) =>
      isNil(value) || Number.isInteger(value) || t('validation.integer_required'),
  };

  const inactivityTimeoutRules = {
    validate: (value?: number | null) => {
      if (!getValues('inactivity_enabled')) {
        return true;
      }
      if (isNil(value) || !Number.isInteger(value)) {
        return t('validation.integer_required');
      }
      if (value < INACTIVITY_TIMEOUT_MIN_MINUTES) {
        return t('validation.minValue', { 0: INACTIVITY_TIMEOUT_MIN_MINUTES });
      }
      if (value > INACTIVITY_TIMEOUT_MAX_MINUTES) {
        return t('validation.maxValue', { 0: INACTIVITY_TIMEOUT_MAX_MINUTES });
      }
      return true;
    },
  };

  const onRadioChange = useCallback(
    (event) => {
      if (event.target.value === 'true') {
        field.onChange(true);
      } else {
        field.onChange(false);
      }
    },
    [field]
  );

  useEffect(() => {
    let options = [];
    if (integrationType === 'web') {
      options = prioritizeLanguageOptions(
        Object.keys(languageAndFlagByCountryCode).reduce<OptionBase[]>((acc, key) => {
          if (!languageAndFlagByCountryCode[key]?.display_only) {
            return [...acc, { label: formatLanguageLabel(key), value: key }];
          }
          return acc;
        }, [])
      );
      setLanguageOptions([
        { label: t('integrations.web_language.auto_detect'), value: 'default' },
        ...options,
      ]);
    }
  }, [t, integrationType]);

  const updateInactivityToggle = useCallback(
    (onChange: (enabled: boolean) => void) => (enabled: boolean) => {
      onChange(enabled);
      if (!enabled) {
        clearErrors('inactivity_timeout');
      } else if (!getValues('inactivity_timeout')) {
        setValue('inactivity_timeout', INACTIVITY_TIMEOUT_DEFAULT_MINUTES);
      }
    },
    [clearErrors, getValues, setValue]
  );

  const updateSourceLimitToggle = useCallback(() => {
    if (showSourceLimit) {
      setValue('external_urls_limit', undefined, { shouldDirty: true });
    }
    setShowSourceLimit((prev) => !prev);
  }, [setValue, showSourceLimit]);

  return (
    <>
      <Accordion
        title={t('integrations.sticky_session.title')}
        subtitle={t('integrations.sticky_session.subtitle')}
        onSubmit={handleSubmit(handleUpdateIntegration)}
        disabled={!isDirty}
        isLoading={updateStatus === 'pending'}
        expanded={expanded === type}
        handleChange={toggleAccordion(type)}
        readOnly={!canWrite}
        ref={registerAccordion(type, handleSubmit(handleUpdateIntegration), trigger)}
      >
        <Form {...formMethods}>
          {integrationType === 'web' && (
            <>
              <div className={styles.container}>
                <TitleSubtitle
                  title={t('integrations.file_upload.title')}
                  subtitle={t('integrations.file_upload.subtitle')}
                />
                <Controller
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Switch
                        label={t(value ? 'common.enabled' : 'common.disabled')}
                        checked={Boolean(value)}
                        disabled={!integration || !canWrite}
                        onCheckedChange={onChange}
                      />
                    );
                  }}
                  name="file_upload"
                  control={control}
                />
              </div>
              <div className={styles.container}>
                <TitleSubtitle
                  title={t('integrations.floating_window.title')}
                  subtitle={t('integrations.floating_window.subtitle')}
                />
                <Controller
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      label={t(value ? 'common.enabled' : 'common.disabled')}
                      checked={Boolean(value)}
                      disabled={!integration || !canWrite}
                      onCheckedChange={onChange}
                    />
                  )}
                  name="floating_window"
                  control={control}
                />
              </div>
              <div className={cn(styles.sticky, styles.container)}>
                <TitleSubtitle
                  title={t('integrations.sticky_session.conv_hist.title')}
                  subtitle={t('integrations.sticky_session.conv_hist.subtitle')}
                />
                <div className={styles.radioGroup}>
                  {stickyOptions.map((option, index) => (
                    <OptionRadioButton
                      key={`${option.id}-${field.value}`}
                      option={option}
                      size="medium"
                      onChange={onRadioChange}
                      isSelected={field.value === (index === 0 ? true : false)}
                      disableSelectedStyles
                      disabled={!canWrite}
                      alignTop
                    />
                  ))}
                </div>
              </div>
              <div className={styles.container}>
                <FormField
                  control={control}
                  name="resume_timeout"
                  rules={resumeTimeoutRules}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-foreground">
                        {t('integrations.resume_timeout.label')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={0}
                          max={RESUME_TIMEOUT_MAX_MINUTES}
                          step={1}
                          className="w-32"
                          readOnly={!canWrite}
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(e.target.value === '' ? null : Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormDescription>{t('integrations.resume_timeout.help')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <TitleSubtitle
                title={t('integrations.sources_limit_title')}
                subtitle={t('integrations.sources_limit_subtitle')}
              />
              <Switch
                label={t(showSourceLimit ? 'common.enabled' : 'common.disabled')}
                checked={showSourceLimit}
                disabled={!integration || !canWrite}
                onCheckedChange={updateSourceLimitToggle}
              />
              <div className="mt-6">
                {showSourceLimit && (
                  <FormField
                    control={control}
                    name="external_urls_limit"
                    rules={isRequired}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">
                          {t('integrations.sources_select')}
                        </FormLabel>
                        <Select
                          onValueChange={(val) => field.onChange(Number(val))}
                          value={field.value?.toString()}
                          disabled={!canWrite}
                        >
                          <FormControl>
                            <SelectTrigger className="text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {sourcesLimitOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value.toString()}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>

              <div className={styles.container}>
                <TitleSubtitle
                  title={t('integrations.rate.title')}
                  subtitle={t('integrations.rate.subtitle')}
                />

                <Controller
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Switch
                        label={t(
                          value
                            ? 'integrations.fieldsets.active_fieldset.active'
                            : 'integrations.fieldsets.active_fieldset.inactive'
                        )}
                        checked={value}
                        disabled={!integration || !canWrite}
                        onCheckedChange={onChange}
                      />
                    );
                  }}
                  name="rate_responses"
                  control={control}
                />
              </div>

              <div className={styles.container}>
                <TitleSubtitle
                  title={t('integrations.show_survey.title')}
                  subtitle={t('integrations.show_survey.subtitle')}
                />

                <Controller
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Switch
                        label={t(
                          value
                            ? 'integrations.fieldsets.active_fieldset.active'
                            : 'integrations.fieldsets.active_fieldset.inactive'
                        )}
                        checked={value}
                        disabled={!integration || !canWrite}
                        onCheckedChange={onChange}
                      />
                    );
                  }}
                  name="show_survey"
                  control={control}
                />
              </div>

              <div className={styles.container}>
                <TitleSubtitle
                  title={t('integrations.close_minimizes_window.title')}
                  subtitle={t('integrations.close_minimizes_window.subtitle')}
                />

                <Controller
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Switch
                        label={t(
                          value
                            ? 'integrations.fieldsets.active_fieldset.active'
                            : 'integrations.fieldsets.active_fieldset.inactive'
                        )}
                        checked={Boolean(value)}
                        disabled={!integration || !canWrite}
                        onCheckedChange={onChange}
                        data-testid="close_minimizes_window"
                      />
                    );
                  }}
                  name="close_minimizes_window"
                  control={control}
                />
              </div>

              <div className={styles.container}>
                <TitleSubtitle
                  title={t('integrations.inactivity_timeout.title')}
                  subtitle={t('integrations.inactivity_timeout.subtitle')}
                />

                <Controller
                  render={({ field: { onChange, value } }) => {
                    return (
                      <Switch
                        label={t(
                          value
                            ? 'integrations.fieldsets.active_fieldset.active'
                            : 'integrations.fieldsets.active_fieldset.inactive'
                        )}
                        checked={Boolean(value)}
                        disabled={!integration || !canWrite}
                        onCheckedChange={updateInactivityToggle(onChange)}
                        data-testid="inactivity_enabled"
                      />
                    );
                  }}
                  name="inactivity_enabled"
                  control={control}
                />

                {inactivityEnabled && (
                  <div className="mt-6">
                    <FormField
                      control={control}
                      name="inactivity_timeout"
                      rules={inactivityTimeoutRules}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground">
                            {t('integrations.inactivity_timeout.label')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min={INACTIVITY_TIMEOUT_MIN_MINUTES}
                              max={INACTIVITY_TIMEOUT_MAX_MINUTES}
                              step={1}
                              className="w-32"
                              readOnly={!canWrite}
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) =>
                                field.onChange(
                                  e.target.value === '' ? null : Number(e.target.value)
                                )
                              }
                            />
                          </FormControl>
                          <FormDescription>
                            {t('integrations.inactivity_timeout.help')}
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>
              <div className={styles.container}>
                <TitleSubtitle
                  title={t('integrations.web_language.title')}
                  subtitle={t('integrations.web_language.subtitle')}
                  externalLink={{
                    label: t('integrations.web_language.external_link'),
                    url: 'https://docs.moveo.ai/docs/integrations/web/web-configuration#language-settings',
                  }}
                />
                <FormField
                  control={control}
                  name="language"
                  rules={isRequired}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <PopoverCombobox
                          options={languageOptions ?? []}
                          value={field.value || ''}
                          onChange={field.onChange}
                          disabled={!canWrite}
                          className="text-foreground"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </>
          )}
          <div className={styles.container}>
            <TitleSubtitle
              title={t('integrations.security.checkbox.title')}
              subtitle={t('integrations.security.checkbox.subtitle', { 0: integration?.type })}
            />
            <Controller
              render={({ field: { onChange, value } }) => {
                return (
                  <Switch
                    label={t(
                      value
                        ? 'integrations.fieldsets.active_fieldset.active'
                        : 'integrations.fieldsets.active_fieldset.inactive'
                    )}
                    checked={value}
                    disabled={!integration || !canWrite}
                    onCheckedChange={onChange}
                  />
                );
              }}
              name="encryption_activation"
              control={control}
            />
          </div>
          <ContextualHelp
            title={t('integrations.security.help.title')}
            name="encryption"
            links={[
              {
                label: t('integrations.security.help.label'),
                url: getDocsUrl('/docs/ai-agents/responses/webviews'),
              },
            ]}
          >
            {t('integrations.security.help.message')}
          </ContextualHelp>
          {activation && (
            <>
              <div className="pt-2">
                <FormField
                  control={control}
                  name="customer_rsa_key"
                  rules={customerRsaKey}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-1">
                        {t('integrations.security.input_label')}
                        <InfoTooltip content={t('integrations.security.tooltip')} size="large">
                          <CircleHelpIcon className="size-3.5 text-foreground-muted" />
                        </InfoTooltip>
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          value={field.value ?? ''}
                          onBlur={trimOnBlur(field)}
                          placeholder={t('integrations.security.placeholder')}
                          disabled={!canWrite}
                          minRows={2}
                          maxRows={10}
                        />
                      </FormControl>
                      <FormMessage>
                        {errorMessage({
                          field: errors.customer_rsa_key,
                          maxLength: customerRsaKey.maxLength,
                        })}
                      </FormMessage>
                    </FormItem>
                  )}
                />
              </div>
              {generatedPublicKey && (
                <div className={styles.publicKey}>
                  <span className={styles.title}>{t('integrations.security.public_key')}</span>
                  <div className={styles.codeBlockBontainer}>
                    <CodeBlock isPreTag>{generatedPublicKey}</CodeBlock>
                    <CopyButton className={styles.copyPublicKey} data={generatedPublicKey} />
                  </div>
                </div>
              )}
            </>
          )}
        </Form>
      </Accordion>
    </>
  );
};

export default AdvancedSettings;
